import styles from './StoryboardAnalysis.module.less'
import { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import ChartRangeViewLandscape from './components/ChartRangeViewLandscape/ChartRangeViewLandscape'
import ChartRangeViewPortrait from './components/ChartRangeViewPortrait/ChartRangeViewPortrait'
import LegendDot from '../VideoAnalysis/VideoAnalysisChart/components/LegendDot/LegendDot'
import AIcon from '@/components/A/AIcon/AIcon'
import { Divider } from 'antd'
import AnchorSlider from './components/AnchorSlider/AnchorSlider'
import { useInViewport } from 'ahooks'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import PeriodReason from './components/PeriodReason/PeriodReason'
import { useTranslation } from 'react-i18next'
import { commonUtils } from '@/helpers/commonUtils'
import { DASHBOARD_CONTENT_CONTAINER_ID } from '@/router/pageConst'
import { CATEGORY_TAB_ID } from '../CategoryTab/CategoryTab'
import MiddleText from '@/components/MiddleText/MiddleText'
import {
  PROFESSOR_RATE_BTN_ID_PREFIX,
  RateBtn,
} from '@/pages/ProfessorProjectDetailPage/components/ProfessorRatePanel/ProfessorRatePanel'
import { ProfessorContext } from '@/Contexts/Contexts'
import HiddenDetailBox from './components/HiddenDetailBox/HiddenDetailBox'
import AiTip from '@/components/AiTip/AiTip'
import { INTERNAL_HOST } from '@/commonConst'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import cls from 'classnames'

interface Props {
  project: ProjectModels.ProjectDetail
  hasTranslated: boolean
  needDoAnimation: boolean
  onAnimationEnd: () => void
  mode?: 'professor' | 'default'
}

export const genRangeDesc = (range: { startTime: number; endTime: number }) => {
  if (range.startTime + 1 === range.endTime) {
    return `${range.endTime}s`
  }
  return `${range.startTime + 1}s ~ ${range.endTime}s`
}

type RangeKey = Extract<
  Models.ProfessorAnalyseModule,
  'the_golden_3s' | 'key_message_moment' | 'brand_moment' | 'interest_moment' | 'the_ending_3s'
>
function StoryboardAnalysis(props: Props) {
  const { t } = useTranslation()
  const { showProfessorPanel, unfinishedRateBtn } = useContext(ProfessorContext)
  const isPortraitMode = isPortraitByProjectInfo(props.project)
  const durationCategory = props.project.durationCategory
  const adDuration = Number(props.project.adDuration || 0)
  const detailSeconds = props.project.detailMetrics.length
  const shouldUseRegularCreativeLayout =
    durationCategory === 'ten_plus' || adDuration > 10 || detailSeconds > 10
  const shouldUseSplashCreativeLayout =
    durationCategory === 'five_to_ten' || (!shouldUseRegularCreativeLayout && adDuration > 0 && adDuration <= 10)
  const isPortraitSplashCreative = isPortraitMode && shouldUseSplashCreativeLayout
  const isPortraitRegularCreative = isPortraitMode && shouldUseRegularCreativeLayout
  const firstThreeDimension = props.project.dimensionMetrics.firstThree
  const keyInformationDimension = props.project.dimensionMetrics.keyInformation
  const brandConnectionDimension = props.project.dimensionMetrics.brandConnection
  const interestingEnagementDimension = props.project.dimensionMetrics.interestingEnagement
  const lastThreeDimension = props.project.dimensionMetrics.lastThree

  const chartRangeViews: Array<{
    key: RangeKey
    title: string
    icon: ReactNode
    score: number
    meanScore: number
    ranges: Array<{ start: number; end: number }>
    rangeDesc: string
    overallPerformance?: string
    reason: {
      performance?: string
      advantage?: string
      disadvantage?: string
      suggestions?: string
    }
  }> = [
    {
      key: 'the_golden_3s' as RangeKey,
      title: t('dashboard.projectDetailPage.storyboardView.theGolden3Seconds'),
      icon: (
        <AIcon width={20} style={{ position: 'relative', top: -2 }} type='icon-fill-global_key' />
      ),
      score: firstThreeDimension.score,
      meanScore: firstThreeDimension.meanScore,
      ranges: [
        {
          start: firstThreeDimension.startTime,
          end: firstThreeDimension.endTime,
        },
      ],
      rangeDesc: genRangeDesc(firstThreeDimension),
      overallPerformance: firstThreeDimension.reason.performance,
      reason: firstThreeDimension.reason,
    },
    {
      key: 'key_message_moment' as RangeKey,
      title: t('dashboard.projectDetailPage.storyboardView.informationTransmission'),
      icon: (
        <AIcon
          width={20}
          style={{ position: 'relative', top: -2 }}
          type='icon-fill-global_information'
        />
      ),
      score: keyInformationDimension.overall.score,
      meanScore: keyInformationDimension.overall.meanScore,
      ranges: keyInformationDimension.periods
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
          start: item.startTime,
          end: item.endTime,
        })),
      rangeDesc: keyInformationDimension.periods.map((item) => genRangeDesc(item)).join('、'),
      overallPerformance: keyInformationDimension.overall.reason.performance,
      reason: keyInformationDimension.overall.reason,
    },
    {
      key: 'brand_moment' as RangeKey,
      title: t('dashboard.projectDetailPage.storyboardView.brandRelevance'),
      icon: (
        <AIcon width={20} style={{ position: 'relative', top: -2 }} type='icon-fill-global_link' />
      ),
      score: brandConnectionDimension.overall.score,
      meanScore: brandConnectionDimension.overall.meanScore,
      ranges: brandConnectionDimension.periods
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
          // 这里兼容 0s 和 1s 的展示
          start: item.startTime,
          end: item.endTime,
        })),
      rangeDesc: brandConnectionDimension.periods.map((item) => genRangeDesc(item)).join('、'),
      overallPerformance: brandConnectionDimension.overall.reason.performance,
      reason: brandConnectionDimension.overall.reason,
    },
    {
      key: 'interest_moment' as RangeKey,
      title: t('dashboard.projectDetailPage.storyboardView.interestStimulation'),
      icon: (
        <AIcon
          width={20}
          style={{ position: 'relative', top: -2 }}
          type='icon-fill-global_interest'
        />
      ),
      score: interestingEnagementDimension.overall.score,
      meanScore: interestingEnagementDimension.overall.meanScore,
      ranges: interestingEnagementDimension.periods
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
          // 这里兼容 0s 和 1s 的展示
          start: item.startTime,
          end: item.endTime,
        })),
      rangeDesc: interestingEnagementDimension.periods.map((item) => genRangeDesc(item)).join('、'),
      overallPerformance: interestingEnagementDimension.overall.reason.performance,
      reason: interestingEnagementDimension.overall.reason,
    },

    {
      key: 'the_ending_3s' as RangeKey,
      title: t('dashboard.projectDetailPage.storyboardView.theEnding3Seconds'),
      icon: (
        <AIcon width={20} style={{ position: 'relative', top: -2 }} type='icon-fill-global_end' />
      ),
      score: lastThreeDimension.score,
      meanScore: lastThreeDimension.meanScore,
      ranges: [
        {
          start: lastThreeDimension.startTime,
          end: lastThreeDimension.endTime,
        },
      ],
      rangeDesc: genRangeDesc(lastThreeDimension),
      overallPerformance: lastThreeDimension.reason.performance,
      reason: lastThreeDimension.reason,
    },
  ]
    .filter((range) => range.score > 0)
    .filter((range) => {
      if (props.mode === 'professor') {
        /** 专家评测仅展示 前3秒 和 后3秒 */
        const passKey = ['the_golden_3s', 'the_ending_3s']
        return passKey.includes(range.key)
      }
      return true
    })

  const anchorOptions = chartRangeViews.map((range) => {
    return {
      label: range.title,
      key: range.title,
    }
  })

  const root = useRef<HTMLDivElement>(null)

  const chartRangeDoms = useRef<HTMLDivElement[]>([])
  const [activeKey, setActiveKey] = useState(anchorOptions[0].key)

  const [animationCollector, setAnimationCollector] = useState<boolean[]>([])
  const ChartRangeViewComponent =
    isPortraitMode && !isPortraitRegularCreative ? ChartRangeViewPortrait : ChartRangeViewLandscape

  const onAnchorClick = (key: string) => {
    setActiveKey(key)
    const index = anchorOptions.findIndex((option) => option.key === key)
    const targetDom = chartRangeDoms.current[index]
    const containerElement = document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
    const categoryTabElement = document.getElementById(CATEGORY_TAB_ID)!
    const categoryElementOffsetHeight = categoryTabElement.offsetHeight
    if (containerElement) {
      console.log(
        'top',
        targetDom?.offsetTop - containerElement.offsetTop - categoryElementOffsetHeight
      )
      containerElement.scrollTo({
        top: targetDom?.offsetTop - containerElement.offsetTop - categoryElementOffsetHeight,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    if (unfinishedRateBtn.length > 0) {
      setTimeout(() => {
        const el = document.getElementById(
          `${PROFESSOR_RATE_BTN_ID_PREFIX}-${unfinishedRateBtn[0]}`
        )
        if (!el) {
          return
        }
        const containerElement = document.getElementById(DASHBOARD_CONTENT_CONTAINER_ID)
        const categoryTabElement = document.getElementById(CATEGORY_TAB_ID)!
        if (containerElement) {
          const top =
            el.getBoundingClientRect().top -
            categoryTabElement.offsetTop -
            categoryTabElement.offsetHeight
          containerElement.scrollTo({
            top: top + containerElement.scrollTop,
            behavior: 'smooth',
          })
        }
      })
    }
  }, [unfinishedRateBtn])

  useInViewport(chartRangeDoms.current, {
    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    callback: (entry) => {
      const { target, intersectionRatio } = entry
      if (intersectionRatio >= 0.2) {
        const index = chartRangeDoms.current.findIndex((dom) => dom === target)
        setActiveKey(anchorOptions[index].key)
      }
    },
  })

  useEffect(() => {
    if (animationCollector.length >= chartRangeViews.length) {
      props.onAnimationEnd()
    }
  }, [animationCollector])

  return (
    <div className={styles.storyboardAnalysis} ref={root}>
      <div className={styles.chartRangeViewAnalysis}>
        {chartRangeViews.map((range, index) => {
          return (
            <div
              className={styles.chartRangeViewBox}
              key={index}
              ref={(el) => {
                if (el) {
                  chartRangeDoms.current[index] = el
                }
              }}
            >
              <img
                className={styles.bg}
                src={commonUtils.getImagePath('projectDetailPage/range-bg.png')}
                alt=''
              />
              <div className={styles.badge}>
                <span className={styles.score}>{range.score}</span>
                <Divider type='vertical' style={{ marginLeft: 10, marginRight: 10 }} />
                <span className={styles.meanScore}>
                  {t('dashboard.projectDetailPage.storyboardView.norm')}: {range.meanScore}
                </span>
              </div>
              <div className={styles.rangeTitle}>
                {range.icon}
                <span className={styles.rangeTitleText}>
                  <span>{range.title}</span>
                  {props.mode === 'professor' && (
                    <span
                      id={`${PROFESSOR_RATE_BTN_ID_PREFIX}-${commonUtils.underLineToCamelize(range.key)}`}
                    >
                      <RateBtn
                        rateKey={range.key}
                        style={{ marginLeft: 8 }}
                        onClick={() => {
                          showProfessorPanel(range.key)
                        }}
                      />
                    </span>
                  )}
                </span>
                <Divider style={{ background: '#B3B3B3' }} type='vertical' />
                <span className={styles.rangeDescText}>{range.rangeDesc}</span>
              </div>
              <div className={styles.rangeViewBox}>
                <div
                  className={cls(
                    styles.chartLegendLayout,
                    isPortraitSplashCreative && styles.chartLegendLayoutPortrait,
                    isPortraitRegularCreative && styles.chartLegendLayoutPortraitRegular
                  )}
                >
                  <ChartRangeViewComponent
                    className={isPortraitSplashCreative ? styles.chartRangeViewPortraitCompact : undefined}
                    chartClassName={props.mode === 'professor' ? styles.chartBox : ''}
                    project={props.project}
                    ranges={range.ranges}
                    thumbnailMode={isPortraitMode ? 'portrait' : 'default'}
                  />
                  <div
                    className={cls(
                      styles.legendWrap,
                      isPortraitSplashCreative ? styles.portrait : '',
                      isPortraitSplashCreative && styles.legendWrapSide,
                      isPortraitRegularCreative && styles.legendWrapBottomCentered
                    )}
                  >
                    <LegendDot
                      layout={
                        isPortraitSplashCreative
                          ? 'side-list'
                          : isPortraitRegularCreative
                            ? 'centered-row'
                            : 'default'
                      }
                    />
                  </div>
                </div>
                <div className={styles.performance}>
                  <div className={styles.performanceTitle}>
                    <AIcon width={28} type='icon-line-global_ai' />
                    <MiddleText text={t('dashboard.projectDetailPage.storyboardView.keyResult')} />
                  </div>
                  {range.overallPerformance && (
                    <MarkdownRenderer
                      loading={!props.hasTranslated}
                      markdownText={range.overallPerformance}
                      needAnimation={props.needDoAnimation}
                      animationEnd={() => {
                        setAnimationCollector((prev) => [...prev, true])
                      }}
                    />
                  )}
                </div>
                {
                  props.project.isFreeTrial && <div className={styles.performance}>
                    <HiddenDetailBox />
                  </div>
                }
                <PeriodReason
                  key={index}
                  reason={range.reason}
                  hasTranslated={props.hasTranslated}
                  animation={props.needDoAnimation}
                />
              </div>
            </div>
          )
        })}
        {
          INTERNAL_HOST.includes(location.hostname) &&
          <div className={styles.aiTipBox}>
            <AiTip style={{ marginTop: -4 }} />
          </div>
        }
      </div>
      <div className={styles.anchorSliderBox}>
        <AnchorSlider
          options={anchorOptions}
          activeKey={activeKey}
          onAnchorClick={onAnchorClick}
          containerElement={root.current}
        />
      </div>
    </div>
  )
}

export default StoryboardAnalysis
